<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">HR/Reviewer Dashboard</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Performance</a></li>
                    <li class="breadcrumb-item active">HR Review</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistics Cards -->
          <div class="row mb-4">
            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h4 class="mb-1">{{ statistics.total }}</h4>
                      <p class="text-muted mb-0">Total Appraisals</p>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-primary rounded-circle">
                        <i class="mdi mdi-file-document-outline font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h4 class="mb-1">{{ statistics.pendingReview }}</h4>
                      <p class="text-muted mb-0">Pending Review</p>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-warning rounded-circle">
                        <i class="mdi mdi-clock-alert-outline font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h4 class="mb-1">{{ statistics.approved }}</h4>
                      <p class="text-muted mb-0">Approved</p>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-success rounded-circle">
                        <i class="mdi mdi-check-circle-outline font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card card-h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between">
                    <div>
                      <h4 class="mb-1">{{ statistics.avgRating }}</h4>
                      <p class="text-muted mb-0">Avg Final Rating</p>
                    </div>
                    <div class="avatar-sm">
                      <span class="avatar-title bg-info rounded-circle">
                        <i class="mdi mdi-star font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-2">
                      <label class="form-label">Department</label>
                      <select v-model="filters.department" @change="loadAppraisals" class="form-select">
                        <option value="">All Departments</option>
                        <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
                      </select>
                    </div>
                    <div class="col-md-2">
                      <label class="form-label">Status</label>
                      <select v-model="filters.status" @change="loadAppraisals" class="form-select">
                        <option value="">All Statuses</option>
                        <option value="Appraiser Review Complete">Pending My Review</option>
                        <option value="Reviewer Complete">Reviewed</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>
                    <div class="col-md-2">
                      <label class="form-label">Year</label>
                      <select v-model="filters.year" @change="loadAppraisals" class="form-select">
                        <option value="">All Years</option>
                        <option value="2025">2025</option>
                        <option value="2024">2024</option>
                        <option value="2023">2023</option>
                      </select>
                    </div>
                    <div class="col-md-2">
                      <label class="form-label">Rating Range</label>
                      <select v-model="filters.rating" @change="loadAppraisals" class="form-select">
                        <option value="">All Ratings</option>
                        <option value="5">5 - Outstanding</option>
                        <option value="4">4 - Good</option>
                        <option value="3">3 - Average</option>
                        <option value="2">2 - Below Avg</option>
                        <option value="1">1 - Poor</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Search</label>
                      <input
                          type="text"
                          v-model="filters.search"
                          @input="searchAppraisals"
                          class="form-control"
                          placeholder="Employee or manager..."
                      >
                    </div>
                    <div class="col-md-1 d-flex align-items-end">
                      <button @click="clearFilters" class="btn btn-secondary w-100">
                        <i class="mdi mdi-refresh"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Appraisals Table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex justify-content-between mb-3">
                    <h5 class="card-title mb-0">All Appraisals</h5>
                    <button @click="exportToExcel" class="btn btn-sm btn-success">
                      <i class="mdi mdi-microsoft-excel me-1"></i> Export to Excel
                    </button>
                  </div>

                  <!-- Loading State -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-2">Loading appraisals...</p>
                  </div>

                  <!-- Appraisals List -->
                  <div v-else-if="filteredAppraisals.length > 0" class="table-responsive">
                    <table class="table table-hover align-middle table-nowrap">
                      <thead class="table-light">
                      <tr>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Position</th>
                        <th>Period</th>
                        <th>Manager</th>
                        <th>Status</th>
                        <th>Self</th>
                        <th>Manager</th>
                        <th>Final</th>
                        <th class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="appraisal in paginatedAppraisals" :key="appraisal.id">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-sm me-2">
                                <span class="avatar-title rounded-circle bg-primary text-white">
                                  {{ getInitials(appraisal.employeeName) }}
                                </span>
                            </div>
                            <div>
                              <h6 class="mb-0">{{ appraisal.employeeName }}</h6>
                              <small class="text-muted">{{ appraisal.appraisalYear }}</small>
                            </div>
                          </div>
                        </td>
                        <td>{{ appraisal.department }}</td>
                        <td>{{ appraisal.positionTitle }}</td>
                        <td>
                          <small>{{ appraisal.periodStart }}</small><br>
                          <small>{{ appraisal.periodEnd }}</small>
                        </td>
                        <td>
                          <small>{{ appraisal.appraiserName || '-' }}</small>
                        </td>
                        <td>
                            <span :class="getStatusBadgeClass(appraisal.status)" class="badge">
                              {{ appraisal.status }}
                            </span>
                        </td>
                        <td>
                            <span v-if="appraisal.sectionA_SelfOverallRating" class="badge bg-primary">
                              {{ parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(1) }}
                            </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                            <span v-if="appraisal.sectionA_AppraiserOverallRating" class="badge bg-info">
                              {{ parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(1) }}
                            </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td>
                            <span v-if="appraisal.finalOverallRating" class="badge bg-success">
                              {{ parseFloat(appraisal.finalOverallRating).toFixed(1) }}
                            </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-center">
                          <div class="btn-group btn-group-sm">
                            <button
                                @click="viewAppraisal(appraisal.id)"
                                class="btn btn-outline-info"
                                title="View Details"
                            >
                              <i class="mdi mdi-eye"></i>
                            </button>
                            <button
                                v-if="canReview(appraisal.status)"
                                @click="reviewAppraisal(appraisal.id)"
                                class="btn btn-outline-success"
                                title="Final Review"
                            >
                              <i class="mdi mdi-check-circle"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>

                    <!-- Pagination -->
                    <div class="row mt-3">
                      <div class="col-sm-12 col-md-5">
                        <div class="dataTables_info">
                          Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, filteredAppraisals.length) }} of {{ filteredAppraisals.length }} entries
                        </div>
                      </div>
                      <div class="col-sm-12 col-md-7">
                        <div class="dataTables_paginate float-end">
                          <ul class="pagination">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                              <a class="page-link" @click="currentPage--" href="javascript:void(0)">Previous</a>
                            </li>
                            <li
                                v-for="page in totalPages"
                                :key="page"
                                class="page-item"
                                :class="{ active: currentPage === page }"
                            >
                              <a class="page-link" @click="currentPage = page" href="javascript:void(0)">{{ page }}</a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                              <a class="page-link" @click="currentPage++" href="javascript:void(0)">Next</a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-else class="text-center py-5">
                    <i class="mdi mdi-folder-open-outline display-4 text-muted"></i>
                    <h5 class="mt-3">No Appraisals Found</h5>
                    <p class="text-muted">There are no appraisals matching your filters.</p>
                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "HRDashboard",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      appraisals: [],
      filteredAppraisals: [],
      departments: [],
      filters: {
        department: '',
        status: '',
        year: '',
        rating: '',
        search: ''
      },
      statistics: {
        total: 0,
        pendingReview: 0,
        approved: 0,
        avgRating: '0.00'
      },
      currentPage: 1,
      perPage: 10
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.filteredAppraisals.length / this.perPage);
    },

    paginatedAppraisals() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredAppraisals.slice(start, end);
    }
  },

  mounted() {
    this.loadDepartments();
    this.loadAppraisals();
  },

  methods: {
    async loadDepartments() {
      try {
        const data = new FormData();
        data.append("function", "getAllDepartments");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.departments = res.data.data;
        }
      } catch (error) {
        console.error("Failed to load departments:", error);
      }
    },

    async loadAppraisals() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAllAppraisalsForHR");
        data.append("department", this.filters.department);
        data.append("status", this.filters.status);
        data.append("year", this.filters.year);
        data.append("rating", this.filters.rating);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.appraisals = res.data.data;
          this.applyFilters();
          this.calculateStatistics();
        } else {
          this.appraisals = [];
        }
      } catch (error) {
        console.error("Failed to load appraisals:", error);
        this.$toast?.error("Failed to load appraisals") || alert("Failed to load appraisals");
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      let filtered = [...this.appraisals];

      // Search filter
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase();
        filtered = filtered.filter(a =>
            a.employeeName.toLowerCase().includes(search) ||
            a.department.toLowerCase().includes(search) ||
            (a.appraiserName && a.appraiserName.toLowerCase().includes(search))
        );
      }

      this.filteredAppraisals = filtered;
      this.currentPage = 1; // Reset to first page
    },

    searchAppraisals() {
      this.applyFilters();
    },

    clearFilters() {
      this.filters = {
        department: '',
        status: '',
        year: '',
        rating: '',
        search: ''
      };
      this.loadAppraisals();
    },

    calculateStatistics() {
      this.statistics.total = this.appraisals.length;

      this.statistics.pendingReview = this.appraisals.filter(a =>
          a.status === 'Appraiser Review Complete' || a.status === 'Reviewer Pending'
      ).length;

      this.statistics.approved = this.appraisals.filter(a =>
          a.status === 'Approved'
      ).length;

      // Calculate average final rating
      const ratingsWithValues = this.appraisals.filter(a => a.finalOverallRating);
      if (ratingsWithValues.length > 0) {
        const sum = ratingsWithValues.reduce((acc, a) => acc + parseFloat(a.finalOverallRating), 0);
        this.statistics.avgRating = (sum / ratingsWithValues.length).toFixed(2);
      } else {
        this.statistics.avgRating = '0.00';
      }
    },

    viewAppraisal(id) {
      this.$router.push({
        path: '/hr-appraisal-view',
        query: { id: id }
      });
    },

    reviewAppraisal(id) {
      this.$router.push({
        path: '/hr-review-form',
        query: { id: id }
      });
    },

    canReview(status) {
      return status === 'Appraiser Review Complete' || status === 'Reviewer Pending';
    },

    exportToExcel() {
      // Implement Excel export
      this.$toast?.info("Excel export coming soon!") || alert("Excel export coming soon!");
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'bg-secondary',
        'Self-Assessment Complete': 'bg-info',
        'Appraiser Review Pending': 'bg-warning',
        'Appraiser Review Complete': 'bg-warning',
        'Reviewer Pending': 'bg-warning',
        'Reviewer Complete': 'bg-primary',
        'HR/CEO Review Pending': 'bg-primary',
        'Approved': 'bg-success',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
  }
};
</script>

<style scoped>
.card-h-100 {
  height: calc(100% - 0px);
}

.avatar-sm {
  height: 40px;
  width: 40px;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
  font-weight: 600;
}

.table-nowrap td {
  white-space: nowrap;
}

.pagination {
  margin-bottom: 0;
}
</style>